import React, { useState, useEffect, useCallback } from 'react'
import { Drawer, Input, InputNumber, Select, Switch, Spin, message, Table, Popconfirm, Typography, Radio, DatePicker as AntDatePicker } from 'antd'
import { DeleteOutlined, EditOutlined, SaveOutlined, CloseOutlined, CalendarOutlined } from '@ant-design/icons'
import moment from 'moment'
import styled from 'styled-components'
import adminRequest from 'utils/adminRequest'
import Label from 'components/Form/Label'
import Button from 'components/Button'
import CustomDateCalendarDrawer from '../../CreateAssessment/Drawers/CustomDateCalendarDrawer'
import { generateScheduleDates } from 'utils/scheduleUtils'
import {
  countryCodeOptions,
  DEFAULT_COUNTRY_CODE,
  formatMobile,
  nationalNumberLength,
  parseCandidateMobile,
  splitFullMobile,
} from 'utils/candidateMobile'
import { buildRosterMobileUpdates } from './studentMobileEdits'

const { Option } = Select
const { Text } = Typography

const DrawerContent = styled.div`
  padding: 24px;
`

const Section = styled.div`
  margin-bottom: 24px;
`

const FieldRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
`

const FieldCol = styled.div`
  flex: ${props => props.flex || 1};
`

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`

const InfoBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  background: ${props => props.bg || '#f0f0f0'};
  color: ${props => props.color || '#333'};
`

const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
`

const StudentCount = styled.span`
  font-size: 12px;
  color: #6b7280;
  margin-left: 8px;
`

/* Inline mobile editor for a roster row: narrow dial-code Select next to the
   number so the whole control fits inside the Mobile column. */
const InlineMobileEditor = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  .ant-select {
    flex: 0 0 88px;
  }

  .ant-input {
    flex: 1;
    min-width: 80px;
  }
`

const DisabledNotice = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
  font-style: italic;
`

const DateEditModeContainer = styled.div`
  margin-bottom: 16px;
`

const DatesSummary = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 12px;
`

const frequencyOptions = {
  DAILY: [
    { value: 1, label: 'Every Day' },
    { value: 2, label: 'Every 2 Days' },
    { value: 15, label: 'Every 15 Days' },
    { value: 30, label: 'Every 30 Days' },
  ],
  WEEKLY: [
    { value: 'MONDAY', label: 'Every Monday' },
    { value: 'TUESDAY', label: 'Every Tuesday' },
    { value: 'WEDNESDAY', label: 'Every Wednesday' },
    { value: 'THURSDAY', label: 'Every Thursday' },
    { value: 'FRIDAY', label: 'Every Friday' },
    { value: 'SATURDAY', label: 'Every Saturday' },
    { value: 'SUNDAY', label: 'Every Sunday' },
  ],
  MONTHLY: Array.from({ length: 28 }, (_, i) => ({
    value: i + 1,
    label: `On the ${i + 1}${i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'} of the month`,
  })),
  CUSTOM: [],
}

// Validity is now a free-form number input (any positive integer of days)

const EditScheduleDrawer = ({ visible, onClose, scheduleId, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [scheduleData, setScheduleData] = useState(null)

  // Editable fields
  const [scheduleName, setScheduleName] = useState('')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [freqType, setFreqType] = useState(null)
  const [freqValue, setFreqValue] = useState(null)
  const [scheduledDates, setScheduledDates] = useState([]) // the actual dates array stored in DB
  const [validityDays, setValidityDays] = useState(null)
  const [isActive, setIsActive] = useState(true)
  const [students, setStudents] = useState([])

  // Date edit mode: 'frequency' = change frequency to regenerate, 'manual' = pick dates on calendar
  const [dateEditMode, setDateEditMode] = useState(null) // null = no change, 'frequency', 'manual'

  // Custom calendar drawer
  const [showCustomCalendar, setShowCustomCalendar] = useState(false)

  // Student editing
  const [editingStudentIdx, setEditingStudentIdx] = useState(null)
  const [editingEmail, setEditingEmail] = useState('')

  // Mobile edits staged against the roster, keyed by email (roster entries carry
  // no id of their own): { [email]: { countryCode, phone } }.
  const [mobileEdits, setMobileEdits] = useState({})
  const [editingMobileIdx, setEditingMobileIdx] = useState(null)
  const [editCountryCode, setEditCountryCode] = useState(DEFAULT_COUNTRY_CODE)
  const [editMobile, setEditMobile] = useState('')

  // Determine if schedule has already started
  const hasStarted = startDate && moment(startDate).isSameOrBefore(moment(), 'day')

  const fetchSchedule = useCallback(async () => {
    if (!scheduleId) return
    setLoading(true)
    try {
      const response = await adminRequest.get('/assessment/schedule', {
        params: { scheduleId },
      })
      const data = response?.data?.data
      setScheduleData(data)

      // Pre-populate all fields
      setScheduleName(data.scheduleName || '')
      setStartDate(data.scheduleStartDate ? moment(data.scheduleStartDate) : null)
      setEndDate(data.scheduleEndDate ? moment(data.scheduleEndDate) : null)
      setFreqType(data.frequencyType || null)
      setValidityDays(data.assessmentValidityDays || null)
      setIsActive(data.isActive !== undefined ? data.isActive : true)
      setStudents(data.studentList?.studentsData || [])
      setMobileEdits({})
      setEditingMobileIdx(null)

      // The actual dates array from DB
      const dates = Array.isArray(data.frequencyValue) ? data.frequencyValue : []
      setScheduledDates(dates)

      // For non-CUSTOM, freqValue is the first element or the raw value
      if (data.frequencyType !== 'CUSTOM') {
        // Try to extract the original frequency setting
        // For DAILY: interval number, WEEKLY: day name, MONTHLY: day number
        setFreqValue(data.frequencyValue || null)
      } else {
        setFreqValue(null)
      }

      setDateEditMode(null) // reset edit mode
    } catch (error) {
      console.error('Error fetching schedule:', error)
      message.error(error?.response?.data?.message || 'Failed to load schedule details')
    } finally {
      setLoading(false)
    }
  }, [scheduleId])

  useEffect(() => {
    if (visible && scheduleId) {
      fetchSchedule()
    }
    if (!visible) {
      setScheduleData(null)
      setEditingStudentIdx(null)
      setShowCustomCalendar(false)
      setDateEditMode(null)
      setMobileEdits({})
      setEditingMobileIdx(null)
      setEditCountryCode(DEFAULT_COUNTRY_CODE)
      setEditMobile('')
    }
  }, [visible, scheduleId, fetchSchedule])

  // When frequency is changed, regenerate dates
  const handleFrequencyChange = (newFreqType, newFreqValue) => {
    if (startDate && endDate && newFreqType && newFreqValue) {
      const newDates = generateScheduleDates(startDate, endDate, newFreqType, newFreqValue)
      setScheduledDates(newDates)
    }
  }

  const handleSave = async () => {
    if (!scheduleName.trim()) {
      message.warning('Schedule name is required')
      return
    }
    if (!startDate || !endDate) {
      message.warning('Start date and end date are required')
      return
    }
    if (!freqType) {
      message.warning('Frequency type is required')
      return
    }
    if (!validityDays || validityDays < 1) {
      message.warning('Assessment validity must be at least 1 day')
      return
    }

    setSaving(true)
    try {
      const payload = {
        scheduleId,
        scheduleName: scheduleName.trim(),
        scheduleStartDate: startDate.format('YYYY-MM-DD'),
        scheduleEndDate: endDate.format('YYYY-MM-DD'),
        frequencyType: freqType,
        frequencyValue: scheduledDates, // always send the dates array
        assessmentValidityDays: validityDays,
        isActive,
        studentsData: students,
      }

      // Only rows whose number actually changed. Writes the roster (read by every
      // future run) and the assignments of runs already generated.
      const mobileUpdates = buildRosterMobileUpdates(mobileEdits, students)
      if (mobileUpdates.length) payload.updateStudentMobiles = mobileUpdates

      const res = await adminRequest.put('/assessment/schedule', payload)
      const skipped = res?.data?.data?.skippedMobileUpdates
      if (skipped?.length) {
        message.warning(`Mobile not updated for: ${skipped.map(s => s.email).join(', ')}`, 8)
      }
      message.success('Schedule updated successfully')
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error updating schedule:', error)
      message.error(error?.response?.data?.message || 'Failed to update schedule')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteStudent = (idx) => {
    setStudents(prev => prev.filter((_, i) => i !== idx))
  }

  const handleEditStudent = (idx, student) => {
    setEditingStudentIdx(idx)
    setEditingEmail(student.email || student.primary_email || '')
  }

  const handleSaveStudentEmail = (idx) => {
    if (!editingEmail.trim()) {
      message.warning('Email cannot be empty')
      return
    }
    setStudents(prev => prev.map((s, i) => {
      if (i === idx) {
        return { ...s, email: editingEmail.trim(), primary_email: editingEmail.trim() }
      }
      return s
    }))
    setEditingStudentIdx(null)
    setEditingEmail('')
  }

  const handleCancelEdit = () => {
    setEditingStudentIdx(null)
    setEditingEmail('')
  }

  // Roster rows are keyed by email — students_data entries have no id.
  const rosterEmail = (student) =>
    String(student?.email || student?.primary_email || '').trim().toLowerCase()

  // Newer roster entries store the dial code and national number separately;
  // older ones only have `phone`, which may carry the code inline.
  const rosterMobile = (student) => {
    if (student?.countryCode && student?.phone) {
      return { countryCode: student.countryCode, nationalNumber: String(student.phone) }
    }
    return splitFullMobile(student?.phone || '')
  }

  const handleEditMobile = (idx, student) => {
    const staged = mobileEdits[rosterEmail(student)]
    const stored = staged
      ? { countryCode: staged.countryCode, nationalNumber: staged.phone }
      : rosterMobile(student)
    setEditingMobileIdx(idx)
    setEditCountryCode(stored?.countryCode || DEFAULT_COUNTRY_CODE)
    setEditMobile(stored?.nationalNumber || '')
  }

  const handleCancelEditMobile = () => {
    setEditingMobileIdx(null)
    setEditCountryCode(DEFAULT_COUNTRY_CODE)
    setEditMobile('')
  }

  const handleSaveMobile = (student) => {
    const email = rosterEmail(student)
    if (!email) {
      message.warning('This roster entry has no email, so its mobile cannot be updated')
      return
    }
    const entered = editMobile.trim()
    // Blank is a deliberate CLEAR — future runs fall back to the student's profile.
    if (entered) {
      const parsed = parseCandidateMobile(editCountryCode, entered)
      if (!parsed) {
        message.warning(
          `Please enter a valid ${nationalNumberLength(editCountryCode)}-digit mobile number for ${editCountryCode} (without the country code)`
        )
        return
      }
      setMobileEdits(prev => ({
        ...prev,
        [email]: { countryCode: parsed.countryCode, phone: parsed.nationalNumber },
      }))
    } else {
      setMobileEdits(prev => ({ ...prev, [email]: { countryCode: editCountryCode, phone: '' } }))
    }
    handleCancelEditMobile()
  }

  // Count future dates (after today)
  const futureDatesCount = scheduledDates.filter(d => moment(d).isAfter(moment(), 'day')).length
  const pastDatesCount = scheduledDates.length - futureDatesCount

  const studentColumns = [
    {
      title: '#',
      width: 45,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: 'Name',
      width: 120,
      render: (_, record) => {
        const name = [
          record.firstName || record.first_name || '',
          record.lastName || record.last_name || ''
        ].filter(Boolean).join(' ')
        return name || '-'
      },
    },
    {
      title: 'Email',
      width: 180,
      render: (_, record, idx) => {
        if (editingStudentIdx === idx) {
          return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <Input
                size="small"
                value={editingEmail}
                onChange={e => setEditingEmail(e.target.value)}
                onPressEnter={() => handleSaveStudentEmail(idx)}
                style={{ flex: 1 }}
              />
              <SaveOutlined
                style={{ color: '#52c41a', cursor: 'pointer', fontSize: '14px' }}
                onClick={() => handleSaveStudentEmail(idx)}
              />
              <CloseOutlined
                style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '14px' }}
                onClick={handleCancelEdit}
              />
            </div>
          )
        }
        return (
          <span style={{ fontSize: '12px', wordBreak: 'break-all' }}>
            {record.email || record.primary_email || '-'}
          </span>
        )
      },
    },
    {
      title: 'Mobile',
      width: 215,
      render: (_, record, idx) => {
        if (editingMobileIdx === idx) {
          return (
            <InlineMobileEditor>
              <Select
                showSearch
                size="small"
                aria-label="Country dialling code"
                value={editCountryCode}
                options={countryCodeOptions}
                optionFilterProp="searchText"
                onChange={setEditCountryCode}
              />
              <Input
                size="small"
                value={editMobile}
                onChange={e => setEditMobile(e.target.value.replace(/[^\d+\-\s]/g, ''))}
                onPressEnter={() => handleSaveMobile(record)}
                placeholder={`${nationalNumberLength(editCountryCode)} digits`}
                aria-label="Mobile number without country code"
                type="tel"
                maxLength={16}
              />
              <SaveOutlined
                role="button"
                tabIndex={0}
                aria-label="Save mobile number"
                style={{ color: '#52c41a', cursor: 'pointer', fontSize: '14px' }}
                onClick={() => handleSaveMobile(record)}
                onKeyDown={e => e.key === 'Enter' && handleSaveMobile(record)}
              />
              <CloseOutlined
                role="button"
                tabIndex={0}
                aria-label="Cancel mobile edit"
                style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '14px' }}
                onClick={handleCancelEditMobile}
                onKeyDown={e => e.key === 'Enter' && handleCancelEditMobile()}
              />
            </InlineMobileEditor>
          )
        }
        const staged = mobileEdits[rosterEmail(record)]
        const stored = rosterMobile(record)
        const display = staged
          ? formatMobile(staged.countryCode, staged.phone)
          : (stored ? formatMobile(stored.countryCode, stored.nationalNumber) : '')
        return (
          <span style={{ fontSize: '12px', whiteSpace: 'nowrap', color: display ? 'inherit' : '#9ca3af' }}>
            {display || '-'}
            <EditOutlined
              role="button"
              tabIndex={0}
              aria-label={display ? 'Edit mobile number' : 'Add mobile number'}
              style={{ color: '#1890ff', cursor: 'pointer', fontSize: '13px', marginLeft: '8px' }}
              onClick={() => handleEditMobile(idx, record)}
              onKeyDown={e => e.key === 'Enter' && handleEditMobile(idx, record)}
            />
          </span>
        )
      },
    },
    {
      title: '',
      width: 70,
      render: (_, record, idx) => {
        if (editingStudentIdx === idx) return null
        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <EditOutlined
              style={{ color: '#1890ff', cursor: 'pointer', fontSize: '14px' }}
              onClick={() => handleEditStudent(idx, record)}
            />
            <Popconfirm
              title="Remove this student?"
              onConfirm={() => handleDeleteStudent(idx)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '14px' }} />
            </Popconfirm>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <Drawer
        title={null}
        placement="right"
        /* 720, not 560: the student table gained a Mobile column with an inline
           editor (dial code + number + save/cancel) that doesn't fit at 560. */
        width={720}
        onClose={onClose}
        open={visible}
        closable={false}
        headerStyle={{ display: 'none' }}
        bodyStyle={{ padding: 0 }}
        footerStyle={{ padding: 0 }}
        destroyOnClose
        footer={
          <FooterContainer>
            <Button.Secondary text="Cancel" onClick={onClose} />
            <Button.Primary text="Save Changes" onClick={handleSave} disabled={saving} />
          </FooterContainer>
        }
      >
        <Spin spinning={loading}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
          }}>
            <div style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
              Edit Schedule Assessment
            </div>
            <CloseOutlined
              style={{ fontSize: '16px', color: '#6b7280', cursor: 'pointer' }}
              onClick={onClose}
            />
          </div>

          <DrawerContent>
            {/* Assessment Type - Read-only */}
            {scheduleData && (
              <Section>
                <Label>Assessment Type</Label>
                <InfoBadge bg="#EEF2FF" color="#4338CA">
                  {scheduleData.assessmentType}
                </InfoBadge>
                <InfoBadge
                  bg={isActive ? '#ECFDF5' : '#FEF2F2'}
                  color={isActive ? '#059669' : '#DC2626'}
                  style={{ marginLeft: '8px' }}
                >
                  {isActive ? 'Active' : 'Inactive'}
                </InfoBadge>
              </Section>
            )}

            {/* Schedule Name */}
            <Section>
              <Label>Schedule Name *</Label>
              <Input
                value={scheduleName}
                onChange={e => setScheduleName(e.target.value)}
                placeholder="Enter schedule name"
                style={{ fontSize: '14px' }}
              />
            </Section>

            {/* Start Date & End Date */}
            <FieldRow>
              <FieldCol>
                <Label>Schedule Start Date *</Label>
                <AntDatePicker
                  value={startDate}
                  onChange={date => setStartDate(date)}
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  placeholder="Select start date"
                  disabled={hasStarted}
                />
                {hasStarted && (
                  <DisabledNotice>Schedule already started, cannot change start date</DisabledNotice>
                )}
              </FieldCol>
              <FieldCol>
                <Label>Schedule End Date *</Label>
                <AntDatePicker
                  value={endDate}
                  onChange={date => {
                    setEndDate(date)
                    if (!date || !startDate) return
                    // Auto-recalculate dates based on new end date
                    if (dateEditMode === 'frequency' && freqType && freqType !== 'CUSTOM' && freqValue) {
                      const newDates = generateScheduleDates(startDate, date, freqType, freqValue)
                      setScheduledDates(newDates)
                    } else if (freqType && freqType !== 'CUSTOM' && scheduledDates.length >= 2) {
                      const sorted = [...scheduledDates].sort()
                      let inferredValue = null
                      if (freqType === 'DAILY') {
                        const diff = moment(sorted[1]).diff(moment(sorted[0]), 'days')
                        if (diff > 0) inferredValue = diff
                      } else if (freqType === 'WEEKLY') {
                        inferredValue = moment(sorted[0]).format('dddd').toUpperCase()
                      } else if (freqType === 'MONTHLY') {
                        inferredValue = moment(sorted[0]).date()
                      }
                      if (inferredValue) {
                        const newDates = generateScheduleDates(startDate, date, freqType, inferredValue)
                        setScheduledDates(newDates)
                      } else {
                        const newEndStr = date.format('YYYY-MM-DD')
                        setScheduledDates(scheduledDates.filter(d => d <= newEndStr))
                      }
                    } else if (scheduledDates.length > 0) {
                      const newEndStr = date.format('YYYY-MM-DD')
                      setScheduledDates(scheduledDates.filter(d => d <= newEndStr))
                    }
                  }}
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  disabledDate={current => current && startDate && current.isBefore(startDate)}
                  placeholder="Select end date"
                />
              </FieldCol>
            </FieldRow>

            {/* Current Dates Summary */}
            {scheduledDates.length > 0 && (
              <DatesSummary>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>
                      Scheduled Dates
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                      {scheduledDates.length} total ({futureDatesCount} upcoming, {pastDatesCount} past)
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    {freqType}
                  </span>
                </div>
              </DatesSummary>
            )}

            {/* Date Edit Options */}
            <Section style={{ marginTop: '16px' }}>
              <Label>Change Assessment Dates</Label>
              <DateEditModeContainer>
                <Radio.Group
                  value={dateEditMode}
                  onChange={e => {
                    setDateEditMode(e.target.value)
                    // Reset frequency selections when switching
                    if (e.target.value === 'frequency') {
                      setFreqValue(null)
                    }
                  }}
                  style={{ display: 'flex', gap: '12px' }}
                >
                  <Radio
                    value="frequency"
                    style={{
                      border: '1px solid #D0D5DD',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      borderColor: dateEditMode === 'frequency' ? '#444CE7' : '#D0D5DD',
                    }}
                  >
                    Change Frequency
                  </Radio>
                  <Radio
                    value="manual"
                    style={{
                      border: '1px solid #D0D5DD',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      borderColor: dateEditMode === 'manual' ? '#444CE7' : '#D0D5DD',
                    }}
                  >
                    Manually Edit Dates
                  </Radio>
                </Radio.Group>
              </DateEditModeContainer>

              {/* Option 1: Change Frequency */}
              {dateEditMode === 'frequency' && (
                <FieldRow style={{ marginTop: '12px', marginBottom: '0' }}>
                  <FieldCol>
                    <Label>Frequency Type</Label>
                    <Select
                      placeholder="Select Frequency Type"
                      style={{ width: '100%' }}
                      value={freqType}
                      onChange={(value) => {
                        setFreqType(value)
                        setFreqValue(null)
                        // If custom, clear to let user pick manually
                        if (value === 'CUSTOM') {
                          setDateEditMode('manual')
                        }
                      }}
                    >
                      <Option value="DAILY">Every Day</Option>
                      <Option value="WEEKLY">Every Week</Option>
                      <Option value="MONTHLY">Every Month</Option>
                      <Option value="CUSTOM">Custom Dates</Option>
                    </Select>
                  </FieldCol>
                  {freqType && freqType !== 'CUSTOM' && (
                    <FieldCol>
                      <Label>Frequency Value</Label>
                      <Select
                        placeholder="Select Frequency"
                        style={{ width: '100%' }}
                        value={freqValue}
                        onChange={(value) => {
                          setFreqValue(value)
                          // Regenerate dates with new frequency
                          handleFrequencyChange(freqType, value)
                        }}
                      >
                        {(frequencyOptions[freqType] || []).map(opt => (
                          <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))}
                      </Select>
                    </FieldCol>
                  )}
                </FieldRow>
              )}

              {/* Option 2: Manually Edit Dates - open calendar */}
              {dateEditMode === 'manual' && (
                <div style={{ marginTop: '12px' }}>
                  <button
                    type="button"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 16px',
                      border: '1px solid #444CE7',
                      borderRadius: '8px',
                      background: '#EEF2FF',
                      cursor: (!startDate || !endDate) ? 'not-allowed' : 'pointer',
                      opacity: (!startDate || !endDate) ? 0.5 : 1,
                      fontSize: '14px',
                      color: '#444CE7',
                      fontWeight: 500,
                    }}
                    onClick={() => {
                      if (startDate && endDate) setShowCustomCalendar(true)
                    }}
                    disabled={!startDate || !endDate}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CalendarOutlined />
                      {scheduledDates.length > 0
                        ? `${scheduledDates.length} date${scheduledDates.length !== 1 ? 's' : ''} selected - Click to edit`
                        : 'Open Calendar to Pick Dates'
                      }
                    </span>
                    <svg width="20" height="20" fill="none"><rect width="20" height="20" rx="4" fill="#C7D2FE" /><path d="M6 8h8M6 12h4" stroke="#444CE7" strokeWidth="1.5" strokeLinecap="round" /><rect x="3" y="5" width="14" height="12" rx="2" stroke="#444CE7" strokeWidth="1.5" /></svg>
                  </button>
                  {(!startDate || !endDate) && (
                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>
                      Please select start and end dates first
                    </Text>
                  )}
                </div>
              )}
            </Section>

            {/* Validity */}
            <Section>
              <Label>Per Assessment Validity (Days) *</Label>
              <InputNumber
                placeholder="Enter number of days"
                min={1}
                precision={0}
                style={{ width: '100%' }}
                value={validityDays}
                onChange={(value) => setValidityDays(value)}
                addonAfter="days"
              />
            </Section>

            {/* Active Toggle */}
            <Section>
              <Label>Schedule Status</Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Switch
                  checked={isActive}
                  onChange={setIsActive}
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                />
                <span style={{ fontSize: '13px', color: isActive ? '#059669' : '#DC2626' }}>
                  {isActive ? 'Schedule is active and will run as configured' : 'Schedule is paused and will not generate assessments'}
                </span>
              </div>
            </Section>

            {/* Student List */}
            <Section>
              <SectionTitle>
                Student List
                <StudentCount>({students.length} student{students.length !== 1 ? 's' : ''})</StudentCount>
              </SectionTitle>
              <Table
                columns={studentColumns}
                dataSource={students}
                rowKey={(_, idx) => idx}
                size="small"
                pagination={students.length > 10 ? { pageSize: 10, size: 'small' } : false}
                scroll={{ y: 300 }}
                style={{ fontSize: '12px' }}
                locale={{ emptyText: 'No students in this schedule' }}
              />
            </Section>
          </DrawerContent>
        </Spin>
      </Drawer>

      {/* Custom Date Calendar Drawer */}
      {showCustomCalendar && (
        <CustomDateCalendarDrawer
          visible={showCustomCalendar}
          onClose={() => setShowCustomCalendar(false)}
          startDate={startDate}
          endDate={endDate}
          selectedDates={scheduledDates}
          onDatesChange={(selectedDates) => {
            setScheduledDates(selectedDates)
            // If user manually picks dates, set frequency type to CUSTOM
            setFreqType('CUSTOM')
          }}
          minDates={1}
          title="Edit Assessment Dates"
        />
      )}
    </>
  )
}

export default EditScheduleDrawer
